/**
 * chatHelpers — pure helper functions for the WBR Callouts Viewer chatbox.
 *
 * Every function here is pure: it performs no I/O, never mutates its inputs,
 * and (for the validation/parsing helpers) is total — it never throws. These
 * helpers back the chatbox's input validation, identity gating, mutation-input
 * construction, source parsing, and derived UI flags so the React component can
 * stay thin and the logic stays property-testable.
 */

import { AskAI2Input } from "../../../models/API";
import { ChatState, Source } from "./chatReducer";
import { MAX_QUESTION_LEN, MAX_SOURCES } from "./chatConstants";

/**
 * Classification of a raw question string for submission.
 *
 * - `ok`        — submittable; carries the trimmed text.
 * - `empty`     — empty or whitespace-only (Req 2.5).
 * - `too_long`  — trimmed length exceeds `MAX_QUESTION_LEN` (Req 2.6).
 */
export type QuestionValidation =
  | { kind: "ok"; trimmed: string }
  | { kind: "empty" }
  | { kind: "too_long" };

/**
 * Validate a raw question string.
 *
 * Trims leading/trailing whitespace, then classifies:
 * - `""` or whitespace-only → `{ kind: "empty" }` (Req 2.5)
 * - trimmed length > `MAX_QUESTION_LEN` → `{ kind: "too_long" }` (Req 2.6)
 * - otherwise → `{ kind: "ok", trimmed }` (Req 2.1)
 *
 * Total function: never throws for any string input.
 */
export function validateQuestion(raw: string): QuestionValidation {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { kind: "empty" };
  }
  if (trimmed.length > MAX_QUESTION_LEN) {
    return { kind: "too_long" };
  }
  return { kind: "ok", trimmed };
}

/**
 * Type guard for a valid numeric identity (user id / team id).
 *
 * Returns true iff `value` is a finite integer strictly greater than zero
 * (Req 7.2, 7.3, 7.6). null, undefined, zero, negatives, non-integers, `NaN`,
 * and `Infinity` are all invalid.
 */
export function isValidId(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

/**
 * Build the `askAI2` mutation input from the chatbox props and conversation
 * state.
 *
 * Sets `question`, `user_id`, `team_id`, and `user_name` verbatim from the
 * provided values (Req 2.2, 7.1). The `question` is assumed already trimmed by
 * the caller. Omits `conversation_id` when `conversationId` is null or the
 * empty string (Req 3.1); includes it otherwise (Req 3.4).
 *
 * Optional resource/attachment fields (parity with the Ask AI panel):
 * - `dataSourceIds` — when an array, sent as `data_source_ids` so the backend
 *   scopes tools to the user's selection (three-case semantics: omitted = all,
 *   `[]` = none, `[ids]` = filtered). Omitted entirely when not an array.
 * - `skills` — when a non-empty array, sent as `skills` (instruction skills).
 * - `pdfS3Key` / `pdfFilename` — when a non-empty key is provided, attach the
 *   uploaded PDF (`pdf_s3_key` + `pdf_filename`).
 */
export function buildAskAI2Input(args: {
  question: string;
  userId: number;
  teamId: number;
  userName: string;
  conversationId: string | null;
  dataSourceIds?: number[] | null;
  skills?: string[] | null;
  pdfS3Key?: string | null;
  pdfFilename?: string | null;
  viewContext?: unknown;
  viewCallouts?: unknown;
}): AskAI2Input & { user_name?: string } {
  const {
    question,
    userId,
    teamId,
    userName,
    conversationId,
    dataSourceIds,
    skills,
    pdfS3Key,
    pdfFilename,
    viewContext,
    viewCallouts,
  } = args;
  const input: AskAI2Input & { user_name?: string } = {
    question,
    user_id: userId,
    team_id: teamId,
    user_name: userName,
  };
  if (typeof conversationId === "string" && conversationId.length > 0) {
    input.conversation_id = conversationId;
  }
  if (Array.isArray(dataSourceIds)) {
    input.data_source_ids = dataSourceIds;
  }
  if (Array.isArray(skills) && skills.length > 0) {
    input.skills = skills;
  }
  if (typeof pdfS3Key === "string" && pdfS3Key.length > 0) {
    input.pdf_s3_key = pdfS3Key;
    if (typeof pdfFilename === "string" && pdfFilename.length > 0) {
      input.pdf_filename = pdfFilename;
    }
  }
  // WBR view context: what the user is currently looking at. Serialized to
  // AWSJSON strings and only attached when present (omitted for the Ask AI
  // panel and any caller that has no viewer). Serialization is defensive so a
  // bad value can never block sending the question.
  const vcStr = serializeViewField(viewContext);
  if (vcStr !== undefined) {
    input.view_context = vcStr;
  }
  const vcalStr = serializeViewField(viewCallouts);
  if (vcalStr !== undefined) {
    input.view_callouts = vcalStr;
  }
  return input;
}

/**
 * Serialize an optional view-context value to a JSON string for the AWSJSON
 * wire field. Returns undefined when the value is null/undefined or cannot be
 * serialized, so the field is omitted rather than sent as invalid JSON.
 */
function serializeViewField(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
}

/**
 * Map a single known source key to a `Source` field.
 *
 * Known label keys: `name`, `title`, `label`.
 * Known detail keys: `detail`, `page`, `url`.
 */
function coerceToString(value: unknown): string | undefined {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return undefined;
}

/**
 * Normalize a single parsed array entry into a `Source`, or return null when it
 * cannot be normalized.
 *
 * - a string entry → `{ label: entry }`
 * - an object entry → map known keys (`name`/`title`/`label` → label,
 *   `detail`/`page`/`url` → detail) into a `Source`
 * - anything else → null (dropped)
 */
function normalizeSourceEntry(entry: unknown): Source | null {
  if (typeof entry === "string") {
    return { label: entry };
  }
  if (entry !== null && typeof entry === "object" && !Array.isArray(entry)) {
    const obj = entry as Record<string, unknown>;
    const label =
      coerceToString(obj.name) ??
      coerceToString(obj.title) ??
      coerceToString(obj.label);
    const detail =
      coerceToString(obj.detail) ??
      coerceToString(obj.page) ??
      coerceToString(obj.url);
    if (label === undefined && detail === undefined) {
      return null;
    }
    const source: Source = { label: label ?? "" };
    if (detail !== undefined) {
      source.detail = detail;
    }
    return source;
  }
  return null;
}

/**
 * Parse the AWSJSON `sources` string from an `AskAI2Response` into a capped,
 * normalized array of `Source` items.
 *
 * Defensive and total: returns `[]` for null/undefined/empty input or invalid
 * JSON, and never throws (Req 4.4). Non-array JSON values also yield `[]`.
 * Array entries are normalized (string or object), unrecognized entries are
 * dropped, and the result is capped at `MAX_SOURCES`.
 */
export function parseSources(rawSources: string | null | undefined): Source[] {
  if (typeof rawSources !== "string" || rawSources.trim().length === 0) {
    return [];
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawSources);
  } catch {
    return [];
  }

  if (!Array.isArray(parsed)) {
    return [];
  }

  const sources: Source[] = [];
  for (const entry of parsed) {
    if (sources.length >= MAX_SOURCES) {
      break;
    }
    const normalized = normalizeSourceEntry(entry);
    if (normalized !== null) {
      sources.push(normalized);
    }
  }
  return sources;
}

/**
 * Derived gate for whether the Send_Action is enabled.
 *
 * True iff the chatbox is not pending, both identities are valid positive
 * integers, and the current input validates as `ok` (Req 2.6, 5.2, 7.2, 7.3,
 * 7.4, 7.6).
 */
export function computeSendEnabled(args: {
  status: ChatState["status"];
  userId: unknown;
  teamId: unknown;
  input: string;
}): boolean {
  const { status, userId, teamId, input } = args;
  return (
    status !== "pending" &&
    isValidId(userId) &&
    isValidId(teamId) &&
    validateQuestion(input).kind === "ok"
  );
}

/**
 * Whether the empty-state placeholder should be shown.
 *
 * True iff the conversation has zero messages (Req 1.3).
 */
export function shouldShowPlaceholder(state: ChatState): boolean {
  return state.messages.length === 0;
}
